import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Factory, ShoppingCart, Wrench, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/8956445/pexels-photo-8956445.jpeg";

export default function Auth({ mode }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "", role: "buyer" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let user;
      if (isRegister) {
        user = await register(form);
        toast.success("Account created");
      } else {
        user = await login(form.email, form.password);
        toast.success("Welcome back");
      }
      nav(user.role === "buyer" ? "/buyer" : user.role === "supplier" ? "/supplier" : "/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left / form */}
      <div className="flex flex-col px-6 py-8 lg:px-16">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-7 w-7 bg-primary grid place-items-center rounded-sm"><Factory className="h-4 w-4 text-primary-foreground" /></div>
          <span className="font-heading font-extrabold text-lg tracking-tight">Nepton</span>
        </Link>

        <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
          <div className="label-tech text-brand mb-3">{isRegister ? "Create account" : "Sign in"}</div>
          <h1 className="text-3xl font-bold tracking-tight">{isRegister ? "Join the marketplace" : "Welcome back"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isRegister ? "Set up your workspace as a buyer or supplier." : "Access your quotes, orders and messages."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {isRegister && (
              <>
                <div className="grid grid-cols-2 gap-3" data-testid="role-selector">
                  {[
                    { v: "buyer", label: "I'm a Buyer", icon: ShoppingCart },
                    { v: "supplier", label: "I'm a Supplier", icon: Wrench },
                  ].map((r) => {
                    const Icon = r.icon;
                    const active = form.role === r.v;
                    return (
                      <button type="button" key={r.v} data-testid={`role-${r.v}`} onClick={() => setForm((f) => ({ ...f, role: r.v }))}
                        className={cn("flex flex-col items-start gap-2 p-4 border rounded-sm transition-colors", active ? "border-brand bg-brand-soft" : "border-border hover:border-primary")}>
                        <Icon className={cn("h-5 w-5", active ? "text-brand" : "text-muted-foreground")} />
                        <span className="text-sm font-medium">{r.label}</span>
                      </button>
                    );
                  })}
                </div>
                <Field label="Full name" testid="name">
                  <input required value={form.name} onChange={set("name")} data-testid="input-name" className="input-field" placeholder="Jane Engineer" />
                </Field>
                <Field label="Company">
                  <input value={form.company} onChange={set("company")} data-testid="input-company" className="input-field" placeholder="Acme Robotics" />
                </Field>
              </>
            )}
            <Field label="Email">
              <input required type="email" value={form.email} onChange={set("email")} data-testid="input-email" className="input-field" placeholder="you@company.com" />
            </Field>
            <Field label="Password">
              <input required type="password" value={form.password} onChange={set("password")} data-testid="input-password" className="input-field" placeholder="••••••••" />
            </Field>
            {!isRegister && (
              <div className="flex justify-end -mt-1">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-brand hover:underline">Forgot password?</Link>
              </div>
            )}

            {error && <div data-testid="auth-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2 rounded-sm">{error}</div>}

            <button type="submit" disabled={loading} data-testid="auth-submit"
              className="group w-full flex items-center justify-center gap-2 px-4 py-3 bg-brand text-white rounded-sm hover:bg-brand-dark disabled:opacity-60 transition-colors">
              {loading ? "Please wait…" : isRegister ? "Create account" : "Sign in"}
              {!loading && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />}
            </button>
          </form>

          <div className="mt-6 text-sm text-muted-foreground">
            {isRegister ? (
              <>Already have an account? <Link to="/login" data-testid="switch-login" className="text-brand hover:underline">Sign in</Link></>
            ) : (
              <>New to Nepton? <Link to="/register" data-testid="switch-register" className="text-brand hover:underline">Create an account</Link></>
            )}
          </div>
        </div>
      </div>

      {/* Right / image */}
      <div className="hidden lg:block relative border-l border-border">
        <img src={HERO} alt="Manufacturing" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="absolute bottom-0 p-12">
          <blockquote className="text-white text-2xl font-heading font-medium tracking-tight max-w-md leading-snug">
            "From CAD upload to delivered parts — precision manufacturing without the back-and-forth."
          </blockquote>
        </div>
      </div>

      <style>{`.input-field{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));padding:0.65rem 0.85rem;border-radius:0.25rem;font-size:0.9rem;outline:none;transition:border-color .15s}.input-field:focus{border-color:#EA580C}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-tech text-muted-foreground block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
